import React, { useState } from 'react';
import Hero from '../../../components/Hero/Hero';
import Pathfinder from '../../../components/Pathfinder/Pathfinder';
import Metadata from '../../../components/Metadata/Metadata';
import Section from '../../../components/Section/Section';
import Card from '../../../components/Card/Card';
import GlobalCallout from '../../../components/Global/CalloutDefault';
import { Box, Typography } from '@mui/material';
import Wallpaper from '../../../components/Wallpaper/Wallpaper';
import Accordion from '../../../components/Accordion/Accordion';
import Image from '../../../components/Image/Image';
import ContentSplit from '../../../components/ContentSplit/ContentSplit';
import ListItem from '../../../components/ListItem/ListItem';
import Button from '../../../components/Button/Button';
import { Link } from 'react-router-dom';

const PageContent: React.FC = () => {
  const [expandAll, setExpandAll] = useState(false);

  const toggleAll = () => {
    setExpandAll(!expandAll);
  };
  return (
    <Box>
      <Metadata
        title="Bioinformatics Solutions | General Genomics"
        description="Learn how General Genomics uses AI-driven analytics and proprietary data solutions to advance bioinformatics in government applications like disease detection and genomic research."
      />
      <Hero
        helper="Industries &gt; Government"
        headline="Driving government innovation with bioinformatics and <span class='nowrap'>big data analytics</span>"
      />

      <Section theme="" className="cards-large-icons">
        <h2 className="action-on-view action-fade-in">
          Unlock the Power of Bioinformatics
        </h2>
        <p className="action-on-view action-fade-in">
          Discover how General Genomics harnesses advanced bioinformatics
          solutions and AI-driven analytics to address government agencies&apos;
          unique needs. From disease detection to genomic research, our tools
          enable better decision-making and more effective responses to national
          health and security challenges. Explore real-world applications that
          highlight our expertise and transformative approach. Contact us today
          and see how we can help you meet your mission goals with cutting-edge
          technology.
        </p>
        <Image
          path="/images/library/military-meeting-woman.webp"
          size="wide"
          className="action-on-view action-fade-in"
          alt="This is a wide image."
        />
      </Section>

      <ContentSplit
        image="/images/library/military-mechanic-aircraft-repair.webp"
        position="right"
      >
        <p className="helper action-on-view action-fade-in">
          Tailored for Government
        </p>
        <h2 className="action-on-view action-fade-in">
          Our Bioinformatics Solutions for Government
        </h2>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Advanced Disease Detection:</strong> Leverage AI-powered
            analytics to identify and track diseases, supporting rapid response
            efforts and enhancing national health security.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Genomic Research Excellence:</strong> Collaborate on
            cutting-edge genomic research to drive insights into public health
            issues, diseases, and policy decisions.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Data-Driven Insights:</strong> Our platform provides
            actionable data that supports decision-makers in tackling complex
            health and security challenges with precision.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Proven Public Sector Success:</strong> Join leading
            government agencies already benefiting from our successful
            partnerships and project outcomes in health and security.
          </p>
        </ListItem>
      </ContentSplit>

      <Section theme="dark" className="section-cross-linking">
        <h2 className="action-on-view action-fade-in">
          Explore More About General Genomics
        </h2>
        <p className="action-on-view action-fade-in">
          Discover additional resources and solutions that enhance data
          security, bioinformatics insights, and applications tailored to your
          needs.
        </p>
        <div className="card-group">
          <Card
            icon="security"
            headline="Data Governance & Compliance"
            subhead="Learn how we secure sensitive data and ensure compliance with industry standards."
            btnURL="/our-approach/data-governance-and-compliance"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="dashboard"
            headline="Curo46™: AI-driven Bioinformatics"
            subhead="Explore our flagship AI platform that powers groundbreaking bioinformatics solutions."
            btnURL="/innovation/curo46"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="business"
            headline="Public Sector Solutions"
            subhead="See how bioinformatics is transforming public sector applications for better outcomes."
            btnURL="/industries/government/public-sector-solutions"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
        </div>
      </Section>

      <Wallpaper asset="../../images/library/lab-technicians-group-laptop-7.webp"></Wallpaper>

      <Section>
        <Typography variant="h2" className="action-on-view action-fade-in">
          Frequently asked questions about General Genomics&apos; Bioinformatics
          Solutions
        </Typography>
        <Typography className="action-on-view action-fade-in">
          Discover how General Genomics uses AI-driven bioinformatics to advance
          government health, security, and research through data-driven
          solutions.
        </Typography>
        <Box className="accordion-wrapper action-on-view action-fade-in">
          <Button
            className="accordion-button"
            type="outlined"
            onClick={toggleAll}
            icon={expandAll ? 'expand_less' : 'expand_more'}
            label={expandAll ? 'Collapse All' : 'Expand All'}
          />
          <Accordion
            isOpen={expandAll}
            headline="What bioinformatics solutions does General Genomics offer for government agencies?"
          >
            General Genomics provides AI-driven bioinformatics solutions
            tailored to <Link to="/industries/government">government</Link>{' '}
            needs, including disease detection, genomic research, and advanced
            data processing to support national health and security.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How can General Genomics' bioinformatics platform enhance public sector initiatives?"
          >
            Our proprietary solutions help{' '}
            <Link to="/industries/government">government</Link> agencies drive
            data-driven advancements in health, security, and research, offering
            tools to address complex challenges in these areas.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="Can I see examples of General Genomics' work with government agencies?"
          >
            Yes, we showcase successful projects and partnerships within the
            public sector that demonstrate our expertise in bioinformatics,
            highlighting measurable advancements in national health and
            security.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="What makes General Genomics’ bioinformatics solutions different from other providers?"
          >
            Our AI-powered platform,{' '}
            <Link to="/innovation/curo46">Curo46™,</Link> offers advanced
            analytics and data processing capabilities, specifically designed
            for the unique requirements of government use, ensuring secure and
            effective outcomes.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How can I learn more about General Genomics’ bioinformatics solutions for my agency?"
          >
            We encourage you to contact us to explore how our solutions can be
            tailored to your agency&apos;s needs. Reach out for more details or
            to schedule a consultation.
          </Accordion>
        </Box>
      </Section>

      <Section theme="dark" bgName="lines" className="section-call-to-action">
        <Typography variant="h2" className="action-on-view action-fade-in">
          Why partnering with General Genomics transforms bioinformatics
          research and innovation
        </Typography>
        <Box className="card-group">
          <Card
            icon="check"
            headline="Advanced Solutions in Analytics"
            subhead="General Genomics combines analytics and innovation with cutting-edge algorithms, large language models, machine learning, and predictive analytics to transform synthetic systems and complex data insights."
            className="action-on-view action-fade-in"
          />
          <Card
            icon="check"
            headline="Precision Insights"
            subhead="Harness predictive modeling and genomic data analysis to drive accurate, actionable insights tailored to research and healthcare objectives."
            className="action-on-view action-fade-in action-delay-250"
          />
          <Card
            icon="check"
            headline="Collaborative Expertise"
            subhead="Partner with a team experienced in supporting government initiatives to advance bioinformatics research and public health strategies."
            className="action-on-view action-fade-in action-delay-500"
          />
        </Box>
      </Section>

      <GlobalCallout />

      <Pathfinder />
    </Box>
  );
};

export default PageContent;
